// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import { getLatestCosts } from './impactPerEuro'
import { isBrutoVersie } from '../impactPerEuroMethodiek/methodiekVersie'

/**
 * @typedef {object} KengetalJaarRij
 * @property {number} year - Boekjaar
 * @property {number} totalCosts - Totale kosten van dat jaar (euro's)
 * @property {number|null} kengetal - Vastgelegd snapshot, of het live kengetal voor het huidige jaar; anders null
 * @property {string|null} statusLabel - 'vastgelegd (v…)' voor snapshots, de live status voor het huidige jaar, anders null
 * @property {boolean} isCurrent - Het jaar waar de KPI-kaart op rekent
 * @property {boolean} vastgelegd - true wanneer het jaar een Kengetal-snapshot draagt (§ 5.3: nooit herrekenen)
 * @property {boolean} bruto - true voor een snapshot van vóór methodiek v1.2 (teller = bruto jaarbedrag, § 6.2)
 */

/**
 * @typedef {object} ImpactPerEuroBreakdown
 * @property {object|null} latest - De nieuwste CompagnyCosts-rij (het rapportagejaar)
 * @property {Array<KengetalJaarRij>} years - Alle kostenjaren, nieuwste eerst
 */

/**
 * Bouwt het per-jaar-overzicht voor de detailpagina volgens methodiek § 5.3:
 * vastgelegde jaren tonen hun snapshot (geen herberekening met terugwerkende
 * kracht), alleen het huidige rapportagejaar rekent live; oudere jaren zonder
 * snapshot tonen geen kengetal — de teller van toen is niet reconstrueerbaar.
 *
 * @param {object} input
 * @param {import('../impactPerEuroMethodiek/computeImpactPerEuro').KengetalResultaat|null} input.result - Live resultaat voor het rapportagejaar
 * @param {Array<object>} input.compagnyCosts - Alle CompagnyCosts-rijen
 * @returns {ImpactPerEuroBreakdown}
 */
const buildImpactPerEuroBreakdown = ({ result, compagnyCosts } = {}) => {
    const rows = Array.isArray(compagnyCosts) ? compagnyCosts : []
    const latest = getLatestCosts(rows)

    const years = rows
        .map((row) => {
            const year = Number(row?.Year)
            if (!Number.isFinite(year)) return null

            const totalCosts = Number(row?.TotalCosts)
            const isCurrent = Boolean(latest) && year === Number(latest.Year)

            const snapshotValue = Number(row?.Kengetal?.Value)
            const vastgelegd = Boolean(row?.Kengetal) && Number.isFinite(snapshotValue)

            let kengetal = null
            let statusLabel = null
            let bruto = false
            if (vastgelegd) {
                kengetal = snapshotValue
                // Vóór methodiek v1.2 was de teller het bruto jaarbedrag (§ 6.2)
                bruto = isBrutoVersie(row.Kengetal.MethodiekVersie)
                statusLabel = `vastgelegd (v${row.Kengetal.MethodiekVersie || '?'}${bruto ? ', bruto' : ''})`
            } else if (isCurrent && result) {
                kengetal = result.kengetal
                statusLabel = result.status
            }

            return {
                year,
                totalCosts: Number.isFinite(totalCosts) ? totalCosts : 0,
                kengetal,
                statusLabel,
                bruto,
                isCurrent,
                vastgelegd,
            }
        })
        .filter(Boolean)
        .sort((a, b) => b.year - a.year)

    return { latest, years }
}

export default buildImpactPerEuroBreakdown
